// ─────────────────────────────────────────────────────────────────────────────
// Mock data — Notifications
// Exam Management feed for the shell bell. Live import jobs upsert into the
// same store (`lib/notifications-store.ts`).
// ─────────────────────────────────────────────────────────────────────────────

export type NotificationKind = "import" | "assessment" | "submission"
export type NotificationVariant = "info" | "success" | "warning"

export interface NotificationItem {
  id: string
  kind: NotificationKind
  title: string
  description: string
  /** Relative, human-readable — swap for real timestamps once there's a feed. */
  timestamp: string
  read: boolean
  /**
   * Cleared when the sheet opens. The bell badge counts unseen, not unread,
   * so opening the panel does not wipe the list.
   */
  seen: boolean
  variant: NotificationVariant
  /** Optional deep link — clicking the row navigates here when present. */
  href?: string
}

export const NOTIFICATION_KIND_ICON: Record<NotificationKind, string> = {
  import: "fa-file-import",
  assessment: "fa-clipboard-list",
  submission: "fa-inbox",
}

export const MOCK_NOTIFICATIONS: NotificationItem[] = [
  {
    id: "notif-import-review",
    kind: "import",
    title: "12 questions found in Midterm_Blueprint.pdf",
    description: "Nothing is in the pool yet. Review them first.",
    timestamp: "4m ago",
    read: false,
    seen: false,
    variant: "success",
    href: "/exam-management/question-bank/import-review",
  },
  {
    id: "notif-import-direct",
    kind: "import",
    title: "8 drafts imported from Anatomy_quiz.docx",
    description: "Open Imported questions to add any to the section.",
    timestamp: "22m ago",
    read: false,
    seen: false,
    variant: "success",
    href: "/exam-management/question-bank",
  },
  {
    id: "notif-assessment-live",
    kind: "assessment",
    title: "PHY-215 Schrödinger Equation Quiz is live",
    description: "Students can start the assessment.",
    timestamp: "1h ago",
    read: false,
    seen: false,
    variant: "info",
    href: "/exam-management/assessments/a6",
  },
  {
    id: "notif-import-empty",
    kind: "import",
    title: "No questions found in Lecture_notes.pdf",
    description: "We look for numbered items with answer options.",
    timestamp: "1h ago",
    read: false,
    seen: false,
    variant: "info",
    href: "/exam-management/question-bank",
  },
  {
    id: "notif-submissions",
    kind: "submission",
    title: "18 of 42 submissions are in",
    description: "PHY-215 Schrödinger Equation Quiz.",
    timestamp: "2h ago",
    read: false,
    seen: false,
    variant: "info",
    href: "/exam-management/submissions",
  },
  {
    id: "notif-import-failed",
    kind: "import",
    title: "Could not read OSCE_Station_3.pdf",
    description: "The read stopped partway. Nothing was added to the pool.",
    timestamp: "3h ago",
    read: false,
    seen: false,
    variant: "warning",
    href: "/exam-management/question-bank",
  },
  {
    id: "notif-results",
    kind: "assessment",
    title: "Results published for PHY-101 Kinematics Test",
    description: "Average score is ready to review.",
    timestamp: "Yesterday",
    read: true,
    seen: true,
    variant: "success",
    href: "/exam-management/assessments/a12",
  },
]

export function unreadNotificationCount(
  items: NotificationItem[] = MOCK_NOTIFICATIONS
): number {
  return items.filter((item) => !item.read).length
}

export function unseenNotificationCount(
  items: NotificationItem[] = MOCK_NOTIFICATIONS
): number {
  return items.filter((item) => !item.seen).length
}
